import React, { useState, useContext, useEffect } from "react";
import {
  View,
  Text,
  Modal,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  useWindowDimensions,
  Platform,
} from "react-native";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../firebase"; // your Firebase config
import { AuthContext } from "../Context/AuthContext"; // make sure path is correct
import GlobalStyles from "./StylenColor";
import DownloadReport from "./PersonalReport";
import { FontAwesome5, Ionicons } from "@expo/vector-icons";

import {
  widthPercentageToDP as wp,
  heightPercentageToDP as hp,
} from "react-native-responsive-screen";

// Desktop Theme colors (inline to avoid circular dependency)
const DesktopColors = {
  primary: '#059669',
  primaryHover: '#047857',
  cardBg: '#ffffff',
  contentBg: '#f8fafc',
  textPrimary: '#0f172a',
  textSecondary: '#64748b',
  textMuted: '#94a3b8',
  border: '#e2e8f0',
  success: '#22c55e',
  error: '#ef4444',
  warning: '#f59e0b',
};

// Get score color based on value
const getScoreColor = (score) => {
  if (score >= 80) return DesktopColors.success;
  if (score >= 50) return DesktopColors.warning;
  return DesktopColors.error;
};

// Desktop Score Popup Component
const DesktopScorePopup = ({
  visible,
  onClose,
  topic,
  prevScore,
  currScore,
  prevDate,
  currDate,
  selectedQuarter,
  assignedEmployeeInfo,
  handleShowQA,
}) => {
  const [hoveredBtn, setHoveredBtn] = useState(null);
  const currScoreNum = parseInt(currScore) || 0;
  const prevScoreNum = parseInt(prevScore) || 0;
  const scoreDiff = currScoreNum - prevScoreNum;

  if (!visible) return null;

  return (
    <Modal visible={visible} transparent animationType="fade">
      <View style={desktopStyles.overlay}>
        <View style={desktopStyles.popup}>
          {/* Header */}
          <View style={desktopStyles.popupHeader}>
            <View style={desktopStyles.headerContent}>
              <Ionicons name="stats-chart" size={24} color={DesktopColors.primary} />
              <Text style={desktopStyles.popupTitle}>{topic}</Text>
            </View>
            <TouchableOpacity
              style={[desktopStyles.closeIconBtn, hoveredBtn === 'close-icon' && desktopStyles.closeIconBtnHover]}
              onPress={onClose}
              onMouseEnter={() => setHoveredBtn('close-icon')}
              onMouseLeave={() => setHoveredBtn(null)}
            >
              <Ionicons name="close" size={24} color={DesktopColors.textSecondary} />
            </TouchableOpacity>
          </View>

          {/* Content */}
          {currDate === "NOT FILLED for this quarter" ? (
            <View style={desktopStyles.notFilledContent}>
              <View style={desktopStyles.warningBox}>
                <Ionicons name="alert-circle" size={48} color={DesktopColors.warning} />
                <Text style={desktopStyles.notFilledTitle}>Not Evaluated</Text>
                <Text style={desktopStyles.notFilledSubtitle}>This topic has not been evaluated for {selectedQuarter}</Text>
              </View>

              {assignedEmployeeInfo === "not_chosen" ? (
                <View style={desktopStyles.assignmentBox}>
                  <Ionicons name="person-outline" size={20} color={DesktopColors.error} />
                  <Text style={desktopStyles.notChosenText}>This topic is not assigned to any employee</Text>
                </View>
              ) : assignedEmployeeInfo && (
                <View style={desktopStyles.assignmentBox}>
                  <Ionicons name="person" size={20} color={DesktopColors.primary} />
                  <Text style={desktopStyles.assignedText}>
                    Assigned to: {assignedEmployeeInfo.name} (ID: {assignedEmployeeInfo.id})
                  </Text>
                </View>
              )}

              <TouchableOpacity
                style={[desktopStyles.closeBtn, hoveredBtn === 'close' && desktopStyles.closeBtnHover]}
                onPress={onClose}
                onMouseEnter={() => setHoveredBtn('close')}
                onMouseLeave={() => setHoveredBtn(null)}
              >
                <Text style={desktopStyles.closeBtnText}>Close</Text>
              </TouchableOpacity>
            </View>
          ) : (
            <View style={desktopStyles.scoreContent}>
              {/* Score Cards */}
              <View style={desktopStyles.scoreCardsRow}>
                <View style={desktopStyles.scoreCard}>
                  <Text style={desktopStyles.scoreCardLabel}>Current Score</Text>
                  <Text style={[desktopStyles.scoreCardValue, { color: getScoreColor(currScoreNum) }]}>
                    {currScore ? `${currScore}%` : 'N/A'}
                  </Text>
                  <Text style={desktopStyles.scoreCardDate}>{currDate || 'N/A'}</Text>
                </View>

                <View style={desktopStyles.scoreDiffCard}>
                  {scoreDiff !== 0 && prevScore && (
                    <>
                      <Ionicons
                        name={scoreDiff > 0 ? "trending-up" : "trending-down"}
                        size={28}
                        color={scoreDiff > 0 ? DesktopColors.success : DesktopColors.error}
                      />
                      <Text style={[
                        desktopStyles.scoreDiffValue,
                        { color: scoreDiff > 0 ? DesktopColors.success : DesktopColors.error }
                      ]}>
                        {scoreDiff > 0 ? '+' : ''}{scoreDiff}%
                      </Text>
                    </>
                  )}
                </View>

                <View style={desktopStyles.scoreCard}>
                  <Text style={desktopStyles.scoreCardLabel}>Previous Score</Text>
                  <Text style={[desktopStyles.scoreCardValue, { color: prevScore ? getScoreColor(prevScoreNum) : DesktopColors.textMuted }]}>
                    {prevScore ? `${prevScore}%` : 'N/A'}
                  </Text>
                  <Text style={desktopStyles.scoreCardDate}>{prevDate || 'N/A'}</Text>
                </View>
              </View>

              {/* Download Report */}
              <View style={desktopStyles.downloadSection}>
                <DownloadReport
                  topic={topic}
                  selectedQuarter={selectedQuarter}
                  iconSize={24}
                  iconColor={DesktopColors.primary}
                />
                <Text style={desktopStyles.downloadText}>Download Detailed Report</Text>
              </View>

              {/* Actions */}
              <View style={desktopStyles.actionsRow}>
                <TouchableOpacity
                  style={[desktopStyles.qaBtn, hoveredBtn === 'qa' && desktopStyles.qaBtnHover]}
                  onPress={handleShowQA}
                  onMouseEnter={() => setHoveredBtn('qa')}
                  onMouseLeave={() => setHoveredBtn(null)}
                >
                  <Ionicons name="help-circle-outline" size={20} color="#fff" />
                  <Text style={desktopStyles.qaBtnText}>View Q&A</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[desktopStyles.closeBtn, hoveredBtn === 'close' && desktopStyles.closeBtnHover]}
                  onPress={onClose}
                  onMouseEnter={() => setHoveredBtn('close')}
                  onMouseLeave={() => setHoveredBtn(null)}
                >
                  <Text style={desktopStyles.closeBtnText}>Close</Text>
                </TouchableOpacity>
              </View>
            </View>
          )}
        </View>
      </View>
    </Modal>
  );
};

// Desktop Q&A Modal Component
const DesktopQAModal = ({ visible, onClose, qaData, topic }) => {
  const [hoveredBtn, setHoveredBtn] = useState(null);

  if (!visible || !qaData) return null;

  return (
    <Modal visible={visible} transparent animationType="fade">
      <View style={desktopStyles.overlay}>
        <View style={desktopStyles.qaPopup}>
          {/* Header */}
          <View style={desktopStyles.popupHeader}>
            <View style={desktopStyles.headerContent}>
              <Ionicons name="chatbubbles-outline" size={24} color={DesktopColors.primary} />
              <Text style={desktopStyles.popupTitle}>{topic} - Questions & Answers</Text>
            </View>
            <TouchableOpacity
              style={[desktopStyles.closeIconBtn, hoveredBtn === 'close-icon' && desktopStyles.closeIconBtnHover]}
              onPress={onClose}
              onMouseEnter={() => setHoveredBtn('close-icon')}
              onMouseLeave={() => setHoveredBtn(null)}
            >
              <Ionicons name="close" size={24} color={DesktopColors.textSecondary} />
            </TouchableOpacity>
          </View>

          {/* Department Badge */}
          <View style={desktopStyles.deptBadge}>
            <Ionicons name="business-outline" size={16} color={DesktopColors.primary} />
            <Text style={desktopStyles.deptBadgeText}>{qaData.department}</Text>
          </View>

          {/* Q&A List */}
          <ScrollView style={desktopStyles.qaScrollView} showsVerticalScrollIndicator={true}>
            {qaData.responses.map((item, index) => (
              <View key={index} style={desktopStyles.qaItem}>
                <View style={desktopStyles.questionRow}>
                  <View style={desktopStyles.questionNumber}>
                    <Text style={desktopStyles.questionNumberText}>{index + 1}</Text>
                  </View>
                  <Text style={desktopStyles.questionText}>{item.question}</Text>
                </View>
                <View style={desktopStyles.answerRow}>
                  <Ionicons name="arrow-forward" size={16} color={DesktopColors.primary} />
                  <Text style={desktopStyles.answerText}>{item.selectedOption}</Text>
                </View>
              </View>
            ))}
          </ScrollView>

          {/* Footer */}
          <View style={desktopStyles.qaFooter}>
            <TouchableOpacity
              style={[desktopStyles.closeBtn, hoveredBtn === 'close' && desktopStyles.closeBtnHover]}
              onPress={onClose}
              onMouseEnter={() => setHoveredBtn('close')}
              onMouseLeave={() => setHoveredBtn(null)}
            >
              <Text style={desktopStyles.closeBtnText}>Close</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
};

// Desktop Styles
const desktopStyles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  popup: {
    backgroundColor: DesktopColors.cardBg,
    borderRadius: 16,
    width: 520,
    maxWidth: '90%',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 10 },
    shadowOpacity: 0.2,
    shadowRadius: 20,
    elevation: 10,
  },
  qaPopup: {
    backgroundColor: DesktopColors.cardBg,
    borderRadius: 16,
    width: 700,
    maxWidth: '90%',
    maxHeight: '85%',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 10 },
    shadowOpacity: 0.2,
    shadowRadius: 20,
    elevation: 10,
  },
  popupHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 24,
    borderBottomWidth: 1,
    borderBottomColor: DesktopColors.border,
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    flex: 1,
  },
  popupTitle: {
    fontSize: 18,
    fontFamily: 'LatoBlack',
    color: DesktopColors.textPrimary,
    flex: 1,
  },
  closeIconBtn: {
    padding: 8,
    borderRadius: 8,
  },
  closeIconBtnHover: {
    backgroundColor: DesktopColors.contentBg,
  },
  notFilledContent: {
    padding: 24,
    alignItems: 'center',
  },
  warningBox: {
    alignItems: 'center',
    marginBottom: 20,
  },
  notFilledTitle: {
    fontSize: 20,
    fontFamily: 'LatoBlack',
    color: DesktopColors.textPrimary,
    marginTop: 12,
  },
  notFilledSubtitle: {
    fontSize: 14,
    fontFamily: 'LatoRegular',
    color: DesktopColors.textSecondary,
    marginTop: 4,
  },
  assignmentBox: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: DesktopColors.contentBg,
    padding: 12,
    borderRadius: 8,
    marginBottom: 20,
    width: '100%',
  },
  notChosenText: {
    fontSize: 14,
    fontFamily: 'LatoRegular',
    color: DesktopColors.error,
  },
  assignedText: {
    fontSize: 14,
    fontFamily: 'LatoRegular',
    color: DesktopColors.textPrimary,
  },
  scoreContent: {
    padding: 24,
  },
  scoreCardsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 24,
  },
  scoreCard: {
    flex: 1,
    backgroundColor: DesktopColors.contentBg,
    borderRadius: 12,
    padding: 16,
    alignItems: 'center',
  },
  scoreCardLabel: {
    fontSize: 12,
    fontFamily: 'LatoRegular',
    color: DesktopColors.textMuted,
    marginBottom: 8,
  },
  scoreCardValue: {
    fontSize: 28,
    fontFamily: 'LatoBlack',
    fontWeight: '700',
  },
  scoreCardDate: {
    fontSize: 12,
    fontFamily: 'LatoRegular',
    color: DesktopColors.textMuted,
    marginTop: 8,
  },
  scoreDiffCard: {
    width: 80,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 12,
  },
  scoreDiffValue: {
    fontSize: 16,
    fontFamily: 'LatoBlack',
    fontWeight: '700',
    marginTop: 4,
  },
  downloadSection: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: DesktopColors.contentBg,
    padding: 12,
    borderRadius: 8,
    marginBottom: 20,
  },
  downloadText: {
    fontSize: 14,
    fontFamily: 'LatoRegular',
    color: DesktopColors.primary,
    fontWeight: '600',
  },
  actionsRow: {
    flexDirection: 'row',
    gap: 12,
  },
  qaBtn: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: DesktopColors.primary,
    paddingVertical: 14,
    borderRadius: 8,
  },
  qaBtnHover: {
    backgroundColor: DesktopColors.primaryHover,
  },
  qaBtnText: {
    fontSize: 14,
    fontFamily: 'LatoBlack',
    color: '#ffffff',
    fontWeight: '600',
  },
  closeBtn: {
    flex: 1,
    backgroundColor: DesktopColors.textMuted,
    paddingVertical: 14,
    borderRadius: 8,
    alignItems: 'center',
  },
  closeBtnHover: {
    backgroundColor: DesktopColors.textSecondary,
  },
  closeBtnText: {
    fontSize: 14,
    fontFamily: 'LatoBlack',
    color: '#ffffff',
    fontWeight: '600',
  },
  deptBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: DesktopColors.contentBg,
    paddingVertical: 8,
    paddingHorizontal: 16,
    marginHorizontal: 24,
    marginTop: 16,
    borderRadius: 8,
    alignSelf: 'flex-start',
  },
  deptBadgeText: {
    fontSize: 14,
    fontFamily: 'LatoRegular',
    color: DesktopColors.primary,
    fontWeight: '600',
  },
  qaScrollView: {
    maxHeight: 400,
    paddingHorizontal: 24,
    marginTop: 16,
  },
  qaItem: {
    backgroundColor: DesktopColors.contentBg,
    borderRadius: 8,
    padding: 16,
    marginBottom: 12,
  },
  questionRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    marginBottom: 12,
  },
  questionNumber: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: DesktopColors.primary,
    justifyContent: 'center',
    alignItems: 'center',
  },
  questionNumberText: {
    fontSize: 12,
    fontFamily: 'LatoBlack',
    color: '#ffffff',
    fontWeight: '700',
  },
  questionText: {
    flex: 1,
    fontSize: 14,
    fontFamily: 'LatoRegular',
    color: DesktopColors.textPrimary,
    lineHeight: 22,
  },
  answerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginLeft: 40,
    backgroundColor: DesktopColors.cardBg,
    padding: 10,
    borderRadius: 6,
  },
  answerText: {
    fontSize: 14,
    fontFamily: 'LatoRegular',
    color: DesktopColors.primary,
    fontWeight: '500',
  },
  qaFooter: {
    padding: 24,
    borderTopWidth: 1,
    borderTopColor: DesktopColors.border,
  },
});

const ScorePopup = ({
  visible,
  onClose,
  topic,
  prevScore,
  currScore,
  prevDate,
  currDate,
  selectedQuarter,
}) => {
  const { companyId } = useContext(AuthContext);
  const [qaModalVisible, setQaModalVisible] = useState(false);
  const [qaData, setQaData] = useState(null);
  const [assignedEmployeeInfo, setAssignedEmployeeInfo] = useState(null);
  const { width } = useWindowDimensions();
  const isDesktopWeb = Platform.OS === "web" && width > 1024;

  useEffect(() => {
    const fetchAssignedEmployee = async () => {
      if (currDate === "NOT FILLED for this quarter") {
        try {
          const snapshot = await getDocs(
            query(
              collection(db, `companies/${companyId}/employees`),
              where("topic", "array-contains", topic) // key change
            )
          );

          if (!snapshot.empty) {
            const doc = snapshot.docs[0].data();
            setAssignedEmployeeInfo({
              name: doc.name || "Unknown",
              id: doc.employeeId || "N/A",
            });
          } else {
            console.log("No employee found for topic:", topic);
            setAssignedEmployeeInfo("not_chosen");
          }
        } catch (error) {
          console.error("Error fetching assigned employee:", error);
          setAssignedEmployeeInfo("not_chosen");
        }
      }
    };

    fetchAssignedEmployee();
  }, [currDate, topic, companyId]);
    
  const handleShowQA = async () => {
    try {
      const snapshot = await getDocs(
        collection(db, `companies/${companyId}/questionnaireResponses`)
      );

      const filtered = snapshot.docs
        .map((doc) => doc.data())
        .filter((doc) => doc.topic === topic)
        .sort((a, b) => b.timestamp.toDate() - a.timestamp.toDate());

      if (filtered.length > 0) {
        setQaData(filtered[0]); // latest response
        setQaModalVisible(true);
        onClose(); // close ScorePopup
      } else {
        alert("No response found for this topic.");
      }
    } catch (error) {
      console.error("Error fetching response:", error);
      alert("Error fetching response.");
    }
  };

  // Desktop Web View
  if (isDesktopWeb) {
    return (
      <>
        <DesktopScorePopup
          visible={visible}
          onClose={onClose}
          topic={topic}
          prevScore={prevScore}
          currScore={currScore}
          prevDate={prevDate}
          currDate={currDate}
          selectedQuarter={selectedQuarter}
          assignedEmployeeInfo={assignedEmployeeInfo}
          handleShowQA={handleShowQA}
        />
        <DesktopQAModal
          visible={qaModalVisible}
          onClose={() => setQaModalVisible(false)}
          qaData={qaData}
          topic={topic}
        />
      </>
    );
  }

  // Mobile View
  return (
    <>
      {/* Score Popup Modal */}
      <Modal visible={visible} transparent animationType="fade">
        <View style={styles.overlay}>
          <View style={[styles.popup, isDesktopWeb && { maxWidth: "50%" }]}>
            <Text style={styles.title}>{topic} Score Details</Text>
            {currDate === "NOT FILLED for this quarter" ? (
              <>
                <Text style={[styles.text, { marginBottom: 10 }]}>
                  Not Evaluated for this quarter
                </Text>
                {assignedEmployeeInfo === "not_chosen" ? (
                  <Text style={[styles.nftext, { marginBottom: 15 }]}>
                    This topic is not chosen
                  </Text>
                ) : (
                  assignedEmployeeInfo && (
                    <Text style={[styles.nftext, { marginBottom: 15 }]}>
                      Assigned to: {assignedEmployeeInfo.name} (ID:{" "}
                      {assignedEmployeeInfo.id})
                    </Text>
                  )
                )}
                <TouchableOpacity
                  style={[styles.closeButton, GlobalStyles.btnBgShadow]}
                  onPress={onClose}
                >
                  <Text style={[styles.buttonText, GlobalStyles.btntxtoffset]}>Close</Text>
                </TouchableOpacity>
              </>
            ) : (
              <>
                <Text style={styles.text}>
                  Current Score: {currScore ? `${currScore}%` : "NE"} | Date:{" "}
                  {currDate || "NE"}
                </Text>
                <Text style={styles.text}>
                  Previous Score: {prevScore ? `${prevScore}%` : "NE"} | Date:{" "}
                  {prevDate || "NA"}
                </Text>

                <View style={styles.downloadButtonWrapper}>
                  <DownloadReport
                    topic={topic}
                    selectedQuarter={selectedQuarter}
                    iconSize={28}
                    iconColor="green"
                  />
                  <Text style={styles.downloadText}>Download Report</Text>
                </View>

                <View style={styles.buttonRow}>
                  <TouchableOpacity
                    style={[styles.qnaButton, GlobalStyles.btnBgShadow]}
                    onPress={handleShowQA}
                  >
                    <Text style={[styles.buttonText, GlobalStyles.btntxtoffset]}>Show  Q & A</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[styles.closeButton, GlobalStyles.btnBgShadow]}
                    onPress={onClose}
                  >
                    <Text style={[styles.buttonText, GlobalStyles.btntxtoffset]}>Close</Text>
                  </TouchableOpacity>
                </View>
              </>
            )}
          </View>
        </View>
      </Modal>

      {/* Q&A Modal */}
      <Modal visible={qaModalVisible} transparent animationType="fade">
        <View style={styles.overlay}>
          <View style={styles.qaPopup}>
            <Text style={styles.title}>{topic}</Text>
            {qaData && (
              <ScrollView
                contentContainerStyle={{ paddingBottom: 20 }}
                style={{ flexGrow: 1, marginTop: 10 }}
              >
                <Text style={styles.text}>Department: {qaData.department}</Text>
                {qaData.responses.map((item, index) => (
                  <View key={index} style={styles.qaItem}>
                    <Text style={styles.question}>
                      {index + 1}. {item.question}
                    </Text>
                    <Text style={styles.answer}>
                      <FontAwesome5
                        name="arrow-right"
                        size={14}
                        color="#2f5232"
                      />{" "}
                      {item.selectedOption}
                    </Text>
                  </View>
                ))}
              </ScrollView>
            )}
            <TouchableOpacity
              style={[styles.closeButton, GlobalStyles.btnBgShadow]}
              onPress={() => setQaModalVisible(false)}
            >
              <Text style={[styles.buttonText, GlobalStyles.btntxtoffset]}>Close Q&A</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    </>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.5)",
    justifyContent: "center",
    alignItems: "center",
  },
  popup: {
    backgroundColor: "white",
    padding: 20,
    borderRadius: 10,
    width: "85%",
    alignItems: "center",
    maxHeight: "95%",
    justifyContent: "center",
    alignContent: "center",
  },
  qaPopup: {
    backgroundColor: "white",
    padding: 20,
    borderRadius: 10,
    width: "90%",
    height: "85%",
  },
  title: {
    fontSize: GlobalStyles.fontSize.lg,
    color: GlobalStyles.btnColor.backgroundColor,
    fontFamily: GlobalStyles.GlobalFont.fontFamily,
    marginBottom: 10,
    textAlign: "center",
  },
  text: {
    fontSize: GlobalStyles.fontSize.sm,
    marginBottom: 5,
    fontFamily: GlobalStyles.GlobalFont.fontFamily,
  },
  nftext: {
    color: "red",
    fontSize: GlobalStyles.fontSize.sm,
    marginBottom: 5,
    fontFamily: GlobalStyles.GlobalFont.fontFamily,
  },
  question: {
    fontSize: GlobalStyles.fontSize.topTitle,
    fontFamily: GlobalStyles.inpFont.fontFamily,
    paddingVertical: 1,
    marginBottom: 2,
  },
  answer: {
    fontSize: GlobalStyles.fontSize.topTitle,
    fontFamily: GlobalStyles.inpFont.fontFamily,
    marginBottom: 10,
    paddingVertical: 1,
  },
  downloadButtonWrapper: {
    flexDirection: "row",
    alignItems: "center",
    marginVertical: 15,
  },
  downloadText: {
    marginLeft: 10,
    fontSize: GlobalStyles.fontSize.sm,
    paddingVertical:1,
    fontFamily: GlobalStyles.inpFont.fontFamily,
    color: "green",
  },
  buttonRow: {
    flexDirection: "row",
    justifyContent: "space-around",
    width: "100%",
  },
  qnaButton: {
    backgroundColor: GlobalStyles.btnColor.backgroundColor,
    padding: 10,
    borderRadius: 5,
    marginRight: 10,
  },
  closeButton: {
    backgroundColor: GlobalStyles.btnColor.backgroundColor,
    padding: 10,
    borderRadius: 5,
    alignItems: "center",
  },
  buttonText: {
    color: "white",
    fontSize: GlobalStyles.fontSize.topTitle,
    fontFamily: GlobalStyles.GlobalFont.fontFamily,
  },
  qaItem: {
    marginBottom: 10,
    borderBottomWidth: 0.5,
    borderColor: "#ccc",
    paddingBottom: 5,
  },
});

export default ScorePopup;
